import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { BaseButton, BaseRadio, BaseTooltip } from "src/components";
import { useModal } from "src/recoil/modalState/hook";
import SpaceBaseTable from "../components/SpaceBaseTable";

import ClosedPeriodRegister from "./ClosedPeriodRegister";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Row } from "react-table";
import { deleteClosingRecurrenceAsync, saveReservationPolicyExclusiveAsync } from "src/api/product/product-api";
import { ClosingTimePolicyOwner, ReservationPolicyExclusiveParams } from "src/api/product/product-types";

import useGetClosingTimePolicy from "src/hooks/product/useGetClosingTimePolicy";
import { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { SpaceTypeT } from "src/pages/product/product-types";
import { useToast } from "src/recoil/toast/hook";
import { axiosInstance } from "src/utils";
import {
  CLOSED_DAY_ORDER,
  ClosingRecurrenceRow,
  findDefaultClosingPolicy,
  findFacilityClosingPolicy,
  flattenClosingPolicy,
  formatClosingDate,
  formatClosingTimeLabel,
  formatDaysOfWeekLabel,
  isExclusiveClosingPolicy,
} from "../utils/closingTime";
import ClosedPeriodEdit from "./ClosedPeriodEdit";
import { ReservationProps } from "./ReservationAvailabilityPeriod";

export { DayOfWeekTypeLabel } from "../utils/closingTime";

export const DAYS = CLOSED_DAY_ORDER;

/** 상품이면 productId, 계약이면 contractManageId 로 정책 소유자를 만든다. */
export const toClosingTimePolicyOwner = (serviceType: UnionServiceType, serviceId: string | number): ClosingTimePolicyOwner =>
  serviceType === "SERVICE_PRODUCT" ? { productId: serviceId } : { contractManageId: serviceId };

export const ClosedPeriodColumns = [
  {
    Header: "휴무 기간",
    accessor: "effectiveFrom",
    width: 160,
    Cell: ({ row }: { row: Row<ClosingRecurrenceRow> }) => {
      const { effectiveFrom, effectiveTo } = row.original;

      // 컬럼이 좁아 "시작일 ~" 뒤에서 줄을 바꾼다. 하루짜리는 한 줄로 둔다
      if (!effectiveTo || effectiveFrom === effectiveTo) return <div>{formatClosingDate(effectiveFrom)}</div>;

      return (
        <div>
          {formatClosingDate(effectiveFrom)} ~<br />
          {formatClosingDate(effectiveTo)}
        </div>
      );
    },
  },
  {
    Header: "휴무 요일",
    accessor: "daysOfWeek",
    width: 160,
    Cell: ({ row }: { row: Row<ClosingRecurrenceRow> }) => <div>{formatDaysOfWeekLabel(row.original.daysOfWeek)}</div>,
  },
  {
    Header: "휴무 시간",
    accessor: "unavailableFrom",
    width: 160,
    Cell: ({ row }: { row: Row<ClosingRecurrenceRow> }) => <div>{formatClosingTimeLabel(row.original)}</div>,
  },
  {
    Header: "휴무 사유",
    accessor: "title",
    width: 300,
    Cell: ({ row }: { row: Row<ClosingRecurrenceRow> }) => <div className="w-100 text-left">{row.original.title || "-"}</div>,
  },
  {
    Header: "수정/삭제",
    accessor: "edit",
    width: 160,
    Cell: ({
      row,
      serviceId,
      serviceType,
      isPolicyMode,
      isDefault,
      space,
      spaceType,
      isEditable,
      isRemovable,
    }: {
      row: Row<ClosingRecurrenceRow>;
      serviceId: string;
      serviceType: UnionServiceType;
      spaceType: SpaceTypeT;
      isDefault: boolean;
      isPolicyMode?: boolean;
      space?: any;
      isEditable: boolean;
      isRemovable: boolean;
    }) => {
      const { setAbstractModalZ2, setBaseModal } = useModal();
      const queryClient = useQueryClient();
      const { openToast } = useToast();
      const { mutate } = useMutation({
        mutationFn: (recurrenceId: string) =>
          deleteClosingRecurrenceAsync(axiosInstance, {
            ...toClosingTimePolicyOwner(serviceType, serviceId),
            id: recurrenceId,
            // 기본 정책이면 공용공간 타입, 개별 정책이면 공용공간 ID 로 대상을 지정한다
            ...(isDefault ? { facilityType: spaceType } : { facilityId: space?.id }),
          }),
      });

      return (
        <div className="flex-row flex-center">
          {isEditable && (
            <BaseButton
              title={"수정"}
              className="color-white mr4"
              disabled={isPolicyMode}
              onClick={() =>
                setAbstractModalZ2({
                  isOpen: true,
                  size: "xlarge",
                  children: (
                    <ClosedPeriodEdit
                      closingRecurrence={row.original}
                      serviceId={serviceId}
                      serviceType={serviceType}
                      spaceType={spaceType}
                      space={space}
                      isDefault={isDefault}
                    ></ClosedPeriodEdit>
                  ),
                })
              }
            />
          )}
          {isRemovable && (
            <button
              className="base-trash-btn color-bg-gray h50 w50"
              onClick={() => {
                if (isPolicyMode) return;

                setBaseModal({
                  isOpen: true,
                  btnLeftTitle: "취소",
                  btnRightTitle: "확인",
                  title: `휴무일을 삭제하시겠습니까?`,
                  onClick: () => {
                    // 개별 설정은 공용공간 ID 로 대상 정책을 찾는다. 없으면 잘못된 요청이 나가므로 끊는다
                    if (!row.original.id || (!isDefault && !space?.id)) {
                      openToast({ content: "휴무일 정보를 찾을 수 없습니다.", type: "FAIL" });
                      setBaseModal({ isOpen: false });
                      return;
                    }

                    mutate(row.original.id, {
                      onSuccess: () => {
                        openToast({ content: `삭제하였습니다.` });
                      },
                      // 실패해도 확인 모달은 닫고 목록은 최신 상태로 되돌린다
                      onSettled: () => {
                        queryClient.refetchQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
                        setBaseModal({ isOpen: false });
                      },
                    });
                  },
                });
              }}
            ></button>
          )}
          {!isEditable && !isRemovable && "-"}
        </div>
      );
    },
  },
];

const ClosedPeriod = ({ spaceType, serviceType, serviceId, isDefault, space, isEditable, isRemovable }: ReservationProps) => {
  const { setBaseModal, setAbstractModalZ1, setAbstractModalZ2 } = useModal();
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const owner = toClosingTimePolicyOwner(serviceType, serviceId);

  const { data: closingTimePolicyList } = useGetClosingTimePolicy({ owner });

  // 기본 정책 화면은 공용공간 타입 정책을, 개별 설정 화면은 해당 공간 정책을 본다
  const policy = isDefault ? findDefaultClosingPolicy(closingTimePolicyList, spaceType) : findFacilityClosingPolicy(closingTimePolicyList, space?.id);

  const closedPeriodList = flattenClosingPolicy(policy);

  // 개별 설정 화면에서만 기본/개별을 고른다. exclusive=false(또는 정책 없음)면 기본 설정을 따른다
  const isPolicyMode = isDefault ? false : !isExclusiveClosingPolicy(policy);

  const { mutate } = useMutation({
    mutationFn: (params: ReservationPolicyExclusiveParams) => saveReservationPolicyExclusiveAsync(axiosInstance, params),
  });

  const onChangePolicyMode = (exclusive: boolean) => {
    // 적용 방식 저장(RRVP05/11)은 공용공간 ID 가 필수라 개별 설정 화면에서만 호출된다
    const facilityId: string | number | undefined = space?.id;
    if (!facilityId) return;

    setBaseModal({
      isOpen: true,
      title: "선택한 예약 정책이 바로 저장됩니다",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        mutate(
          { ...owner, policyType: "closing-time-policy", facilityId, exclusive },
          {
            onSuccess: () => {
              openToast({ content: "저장 되었습니다.", isSystemToast: true });
            },
            // 실패해도 확인 모달은 닫고 목록은 최신 상태로 되돌린다
            onSettled: () => {
              queryClient.refetchQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
              setBaseModal({ isOpen: false });
            },
          },
        );
      },
    });
  };

  return (
    <>
      <section className="base-abstract-modal__contents-minmax-height overflow-hidden px30">
        {!isDefault && (
          <>
            <div className="base-abstract-modal__contents-subtitle">
              <span className="required font18 font-weight-semibold">예약 정책 적용 방식</span>
            </div>
            <div className="flex-center h36">
              <BaseRadio
                id={"isPolicyMode_true"}
                name={"isPolicyMode"}
                checked={isPolicyMode === true}
                disabled={!isEditable}
                label="기본설정"
                onChange={() => onChangePolicyMode(false)}
              />
              <BaseTooltip
                className={"mr10 no-wrap"}
                tooltip={
                  (
                    <div>
                      <div>• 예약 기본 정책 설정을 그대로 따릅니다.</div>
                      <div>• 기본 설정이 변경되면 즉시 적용됩니다.</div>
                    </div>
                  ) as any as string
                }
                touchIcon="QUESTION"
              ></BaseTooltip>
              <BaseRadio
                id={"isPolicyMode_false"}
                name={"isPolicyMode"}
                checked={isPolicyMode === false}
                label="개별설정" // 무제한
                disabled={!isEditable}
                onChange={() => onChangePolicyMode(true)}
              />
              <BaseTooltip
                tooltip={
                  (
                    <div>
                      <div>• 예약 기본 정책 설정을 따르지 않고 이 공용 공간에만 적용되는 별도의 예약 정책을 설정합니다.</div>
                      <div>• 기본 설정이 변경되더라도 영향을 받지 않습니다.</div>
                    </div>
                  ) as any as string
                }
                touchIcon="QUESTION"
              ></BaseTooltip>
            </div>
          </>
        )}
        <div className="base-abstract-modal__contents-subtitle">
          <span className="font18 font-weight-semibold">휴무일</span>
          {isEditable && (
            <BaseButton
              title={"+ 휴무일 등록"}
              disabled={isPolicyMode}
              onClick={() => {
                setAbstractModalZ2({
                  size: "xlarge",
                  isOpen: true,
                  children: (
                    <ClosedPeriodRegister spaceType={spaceType} serviceId={serviceId} serviceType={serviceType} isDefault={isDefault} space={space} />
                  ),
                });
              }}
            />
          )}
        </div>

        <section className="base-abstract-modal__contents h400 pt10">
          <SpaceBaseTable
            columns={ClosedPeriodColumns}
            data={closedPeriodList ?? []}
            isPolicyMode={isPolicyMode}
            isDefault={isDefault}
            space={space}
            spaceType={spaceType}
            serviceId={serviceId}
            serviceType={serviceType}
            isEditable={isEditable}
            isRemovable={isRemovable}
          ></SpaceBaseTable>
        </section>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        <BaseButton
          title={"취소"}
          className="color-white"
          onClick={() => {
            setAbstractModalZ1({ isOpen: false });
          }}
        />
      </section>
    </>
  );
};

export default ClosedPeriod;
